import { LoadingSpinner } from "components/LoadingSpinner";
import { ToastContext } from "context/ToastContext";
import { parseDate } from "helpers/date";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { useContext, useMemo, useState } from "react";
import {
  useDisableAccessReviewUsersMutation,
  useGetAccessReviewQuery,
  useKeepAccessReviewUserMutation,
} from "store/queries/users";
import { IAccessReviewRow } from "types/User";

import {
  NOT_REMOVED_AUTOMATICALLY,
  hospitalGroupName,
  otherHospitalNames,
  reasonText,
} from "./accessReview.utils";

// CLE-2765: hospital staff waiting for an access decision — no activity for 30+ days,
// or an email address SendGrid has stopped delivering to. INACTIVE users are removed
// automatically on the Removal date unless they log in or are kept; bounced-only users
// stay here until someone decides.

interface IAccessReviewTableRow extends IAccessReviewRow {
  hospital: string;
}

const formatDate = (value: string | null) =>
  value ? parseDate(value, false, true) : null;

export function AccessReviewTable() {
  const { current: toastElement } = useContext(ToastContext);
  const [selected, setSelected] = useState<IAccessReviewTableRow[]>([]);

  const { data, isLoading, isError } = useGetAccessReviewQuery(null, {
    refetchOnMountOrArgChange: true,
  });
  const [disableUsers, { isLoading: isRemoving }] =
    useDisableAccessReviewUsersMutation();
  const [keepUser] = useKeepAccessReviewUserMutation();

  const rows: IAccessReviewTableRow[] = useMemo(
    () =>
      (data?.data ?? [])
        .map((row) => ({ ...row, hospital: hospitalGroupName(row.accounts) }))
        .sort(
          (a, b) =>
            a.hospital.localeCompare(b.hospital) || a.name.localeCompare(b.name)
        ),
    [data]
  );

  const removeAccess = async (targets: IAccessReviewTableRow[]) => {
    try {
      const { data: results } = await disableUsers(
        targets.map((t) => t.id)
      ).unwrap();
      const failed = results.filter(
        (r) => r.status === "failed" || r.status === "not_found"
      );
      if (failed.length) {
        toastElement?.show({
          severity: "warn",
          summary: "Some users were not removed",
          detail: `${targets.length - failed.length} removed, ${
            failed.length
          } could not be removed. Please try those again.`,
        });
      } else {
        toastElement?.show({
          severity: "success",
          summary: "Access removed",
          detail:
            targets.length === 1
              ? `${targets[0].name} can no longer log in.`
              : `${targets.length} users can no longer log in.`,
        });
      }
      setSelected([]);
    } catch (error) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "Access could not be removed. Please try again.",
      });
    }
  };

  const confirmRemove = (targets: IAccessReviewTableRow[]) => {
    confirmDialog({
      header: "Remove access",
      icon: "pi pi-exclamation-triangle",
      message:
        targets.length === 1
          ? `Remove access for ${targets[0].name} (${targets[0].email})? They will not be able to log in. You can enable them again from the Users tab.`
          : `Remove access for ${targets.length} users? They will not be able to log in. You can enable them again from the Users tab.`,
      acceptLabel: "Remove access",
      rejectLabel: "Cancel",
      accept: () => removeAccess(targets),
    });
  };

  const keep = async (row: IAccessReviewTableRow) => {
    try {
      await keepUser(row.id).unwrap();
      toastElement?.show({
        severity: "success",
        summary: "Kept",
        detail: `${row.name} is off the list for 90 days.`,
      });
    } catch (error) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "Could not keep this user. Please try again.",
      });
    }
  };

  // First load only: a refetch after Keep / Remove updates the table in place, so the
  // page does not flash or lose its scroll position.
  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return (
      <p className="text-red-500">
        The access review list could not be loaded. Please refresh the page.
      </p>
    );
  }

  const now = new Date();

  const nameBody = (row: IAccessReviewTableRow) => {
    const others = otherHospitalNames(row.accounts);
    return (
      <div>
        <div>{row.name}</div>
        {others.length > 0 && (
          <div className="text-500 text-sm">Also: {others.join(", ")}</div>
        )}
      </div>
    );
  };

  const groupHeader = (row: IAccessReviewTableRow) => (
    <span className="font-bold">{row.hospital}</span>
  );

  // SendGrid's own reason (e.g. the SMTP bounce text) is on hover.
  const reasonBody = (row: IAccessReviewTableRow) => (
    <span title={row.emailSuppressionReason ?? undefined}>
      {reasonText(row, now)}
    </span>
  );

  const removalBody = (row: IAccessReviewTableRow) => {
    if (row.accessReviewReason === "EMAIL_SUPPRESSED") {
      return (
        <span
          className="text-500"
          title={NOT_REMOVED_AUTOMATICALLY}
          aria-label={NOT_REMOVED_AUTOMATICALLY}
        >
          <i className="pi pi-info-circle text-xs" />
        </span>
      );
    }
    return formatDate(row.scheduledRemovalAt) ?? "";
  };

  const actionsBody = (row: IAccessReviewTableRow) => (
    <div className="flex gap-2 justify-content-end">
      <Button
        label="Remove access"
        className="p-button-sm p-button-danger p-button-outlined"
        onClick={() => confirmRemove([row])}
      />
      <Button
        label="Keep (90 days)"
        className="p-button-sm p-button-text"
        onClick={() => keep(row)}
      />
    </div>
  );

  return (
    <>
      <p className="mt-0 mb-3 text-600">
        Hospital staff with no activity for 30 days or more, or whose email
        address bounced. Inactive users are removed automatically on the removal
        date unless they log in first or are kept.
      </p>
      <DataTable
        value={rows}
        dataKey="id"
        stripedRows
        responsiveLayout="scroll"
        rowGroupMode="subheader"
        groupRowsBy="hospital"
        sortMode="single"
        sortField="hospital"
        sortOrder={1}
        rowGroupHeaderTemplate={groupHeader}
        selection={selected}
        onSelectionChange={(event) => setSelected(event.value)}
        emptyMessage="Nobody is waiting for review."
      >
        <Column selectionMode="multiple" headerStyle={{ width: "3em" }} />
        <Column header="Name" body={nameBody} />
        <Column field="email" header="Email" />
        <Column
          header="Created"
          body={(row: IAccessReviewTableRow) => formatDate(row.createdAt)}
        />
        <Column
          header="Last login"
          body={(row: IAccessReviewTableRow) =>
            formatDate(row.lastLoginAt) ?? "Never"
          }
        />
        <Column
          header="Last action"
          body={(row: IAccessReviewTableRow) =>
            formatDate(row.lastActionAt) ?? "None"
          }
        />
        <Column header="Reason" body={reasonBody} />
        <Column header="Removal date" body={removalBody} />
        <Column body={actionsBody} />
      </DataTable>
      <div className="flex justify-content-end">
        <Button
          label="Remove access"
          className="p-button-danger p-button-outlined mt-4"
          disabled={!selected.length || isRemoving}
          onClick={() => confirmRemove(selected)}
        />
      </div>
    </>
  );
}
